<script>
	import { onMount } from 'svelte';
	import Navbar from '../components/Navbar.svelte';
	import { Check, X, Play, RefreshCw, ChevronDown, ChevronRight } from 'lucide-svelte';
	import {
		fetchWorkspace,
		fetchGardenerSummary,
		fetchGardenerProposals,
		approveGardenerProposal,
		rejectGardenerProposal,
		fetchGardenerTargets,
		runGardenerFlashSweep
	} from '../lib/api.js';

	export let navigate;

	// ─── State ────────────────────────────────────────────────────────
	let tenantId = null;
	let loading = true;
	let summary = { pending_review: 0, approved: 0, applied: 0, auto_applied: 0, rejected: 0 };
	let signal = null;   // metadata signal from gardener_targets
	let activeStatus = 'pending_review';
	let activeAction = '';  // '' | 'trunk' | 'reparent' | 'merge'
	let proposals = [];
	let expandedId = null;
	let actionInFlight = new Set();
	let sweepRunning = false;
	let sweepResult = null;
	let rejectingId = null;
	let rejectionText = '';
	let errorMessage = '';

	const STATUS_TABS = [
		{ key: 'pending_review', label: 'Pending review' },
		{ key: 'applied', label: 'Applied' },
		{ key: 'rejected', label: 'Rejected' }
	];

	const ACTION_FILTERS = [
		{ key: '', label: 'All' },
		{ key: 'trunk', label: 'Trunk' },
		{ key: 'reparent', label: 'Reparent' },
		{ key: 'merge', label: 'Merge' }
	];

	// ─── Data ─────────────────────────────────────────────────────────
	onMount(async () => {
		try {
			const ws = await fetchWorkspace();
			tenantId = ws.workspace.id;
			await Promise.all([refreshSummary(), refreshSignal(), refreshProposals()]);
		} catch (err) {
			errorMessage = err.message || 'Failed to load Gardener data';
		}
		loading = false;
	});

	async function refreshSummary() {
		if (!tenantId) return;
		summary = await fetchGardenerSummary(tenantId);
	}

	async function refreshSignal() {
		if (!tenantId) return;
		try {
			signal = await fetchGardenerTargets(tenantId, { mode: 'metadata' });
		} catch (err) {
			signal = null;
		}
	}

	async function refreshProposals() {
		if (!tenantId) return;
		const res = await fetchGardenerProposals(tenantId, {
			status: activeStatus,
			action: activeAction || undefined,
			limit: 50
		});
		proposals = res.proposals || [];
	}

	$: if (tenantId && activeStatus) refreshProposals();
	$: if (tenantId && activeAction !== undefined) refreshProposals();

	// ─── Actions ──────────────────────────────────────────────────────

	async function handleApprove(proposalId) {
		if (actionInFlight.has(proposalId)) return;
		actionInFlight.add(proposalId);
		actionInFlight = actionInFlight; // trigger reactivity
		errorMessage = '';
		try {
			await approveGardenerProposal(tenantId, proposalId);
			await Promise.all([refreshSummary(), refreshSignal(), refreshProposals()]);
		} catch (err) {
			errorMessage = 'Approve failed: ' + err.message;
		}
		actionInFlight.delete(proposalId);
		actionInFlight = actionInFlight;
	}

	async function submitReject(proposalId) {
		if (!rejectionText.trim() || rejectionText.trim().length < 5) {
			errorMessage = 'Rejection reason must be at least 5 characters';
			return;
		}
		if (actionInFlight.has(proposalId)) return;
		actionInFlight.add(proposalId);
		actionInFlight = actionInFlight;
		errorMessage = '';
		try {
			await rejectGardenerProposal(tenantId, proposalId, rejectionText.trim());
			rejectingId = null;
			rejectionText = '';
			await Promise.all([refreshSummary(), refreshProposals()]);
		} catch (err) {
			errorMessage = 'Reject failed: ' + err.message;
		}
		actionInFlight.delete(proposalId);
		actionInFlight = actionInFlight;
	}

	async function runSweep() {
		if (sweepRunning || !tenantId) return;
		sweepRunning = true;
		sweepResult = null;
		errorMessage = '';
		try {
			sweepResult = await runGardenerFlashSweep(tenantId, { candidateLimit: 3 });
			await Promise.all([refreshSummary(), refreshSignal(), refreshProposals()]);
		} catch (err) {
			errorMessage = 'Flash sweep failed: ' + err.message;
		}
		sweepRunning = false;
	}

	function toggleExpand(id) {
		expandedId = expandedId === id ? null : id;
	}

	function formatTimestamp(ts) {
		if (!ts) return '';
		const d = new Date(ts);
		const now = new Date();
		const diffHr = (now - d) / 3600000;
		if (diffHr < 1) return Math.max(1, Math.floor(diffHr * 60)) + 'm ago';
		if (diffHr < 24) return Math.floor(diffHr) + 'h ago';
		if (diffHr < 24 * 7) return Math.floor(diffHr / 24) + 'd ago';
		return d.toISOString().slice(0, 10);
	}

	function actionLabel(action) {
		if (action === 'trunk') return 'Trunk';
		if (action === 'reparent') return 'Reparent';
		if (action === 'merge') return 'Merge';
		return action;
	}

	function proposalHeadline(p) {
		if (p.action === 'trunk') {
			return p.payload?.trunk?.concept || 'Trunk proposal';
		}
		if (p.action === 'reparent') {
			return 'Reparent ' + (p.payload?.node_id || '').slice(0, 8);
		}
		if (p.action === 'merge') {
			const n = p.payload?.duplicate_node_ids?.length || 0;
			return `Merge ${n} duplicate${n === 1 ? '' : 's'} → primary`;
		}
		return p.action;
	}
</script>

<Navbar {navigate} active="gardener" on:refresh={() => Promise.all([refreshSummary(), refreshSignal(), refreshProposals()])} />

<div class="gardener-page">
	<header class="gardener-header">
		<div class="header-row">
			<h1>Gardener</h1>
			<div class="header-actions">
				<button class="btn-sweep" on:click={runSweep} disabled={sweepRunning || !tenantId}>
					{#if sweepRunning}
						<RefreshCw size={14} strokeWidth={1.5} class="spin" />
						<span>Sweeping…</span>
					{:else}
						<Play size={14} strokeWidth={1.5} />
						<span>Run Flash sweep</span>
					{/if}
				</button>
			</div>
		</div>
		<p class="subtitle">
			Structure-preserving proposals for the concept tree — trunk creation, reparenting, merge
			of duplicates. Proposals queue here for review; approve or reject each. No tree mutation
			happens until you approve.
		</p>
	</header>

	{#if loading}
		<div class="state-empty">Loading…</div>
	{:else}
		<!-- Summary / signals row -->
		<section class="stats-row">
			<div class="stat">
				<span class="stat-label">Pending review</span>
				<span class="stat-value">{summary.pending_review}</span>
			</div>
			<div class="stat">
				<span class="stat-label">Applied</span>
				<span class="stat-value">{summary.applied}</span>
			</div>
			<div class="stat">
				<span class="stat-label">Rejected</span>
				<span class="stat-value">{summary.rejected}</span>
			</div>
			{#if signal}
				<div class="stat stat-signal">
					<span class="stat-label">Tree signals</span>
					<span class="stat-subvalues">
						<span title="Trunk candidates"><strong>{signal.trunk_signal || 0}</strong>T</span>
						<span title="Merge candidates"><strong>{signal.merge_signal || 0}</strong>M</span>
						<span title="Reparent candidates"><strong>{signal.reparent_signal || 0}</strong>R</span>
					</span>
				</div>
				<div class="stat">
					<span class="stat-label">Active nodes</span>
					<span class="stat-value">{signal.total_active_nodes || 0}</span>
				</div>
			{/if}
		</section>

		{#if sweepResult}
			<div class="sweep-result">
				<span class="sweep-label">Last Flash sweep</span>
				<span class="sweep-body">
					{sweepResult.candidates_evaluated} candidates evaluated,
					{sweepResult.proposals_queued} queued,
					{sweepResult.skipped_by_flash || 0} skipped by rubric,
					{sweepResult.rejected_post_validation || 0} rejected by validation.
				</span>
			</div>
		{/if}

		{#if errorMessage}
			<div class="error-banner">{errorMessage}</div>
		{/if}

		<!-- Tabs -->
		<nav class="tabs">
			{#each STATUS_TABS as tab (tab.key)}
				<button
					class="tab"
					class:active={activeStatus === tab.key}
					on:click={() => { activeStatus = tab.key; expandedId = null; }}
				>
					{tab.label}
					{#if tab.key === 'pending_review'}<span class="tab-count">{summary.pending_review}</span>{/if}
					{#if tab.key === 'applied'}<span class="tab-count">{summary.applied}</span>{/if}
					{#if tab.key === 'rejected'}<span class="tab-count">{summary.rejected}</span>{/if}
				</button>
			{/each}
			<div class="action-filter">
				{#each ACTION_FILTERS as filter}
					<button
						class="action-pill"
						class:active={activeAction === filter.key}
						on:click={() => { activeAction = filter.key; expandedId = null; }}
					>
						{filter.label}
					</button>
				{/each}
			</div>
		</nav>

		<!-- Proposal list -->
		<section class="proposal-list">
			{#if proposals.length === 0}
				<div class="state-empty">No proposals matching these filters.</div>
			{/if}
			{#each proposals as p (p.id)}
				<article class="proposal" class:expanded={expandedId === p.id}>
					<header class="proposal-head" on:click={() => toggleExpand(p.id)}>
						<span class="chevron">
							{#if expandedId === p.id}
								<ChevronDown size={14} strokeWidth={1.5} />
							{:else}
								<ChevronRight size={14} strokeWidth={1.5} />
							{/if}
						</span>
						<span class="action-tag action-{p.action}">{actionLabel(p.action)}</span>
						{#if p.confidence}<span class="conf-tag conf-{p.confidence}">{p.confidence}</span>{/if}
						<span class="proposal-title">{proposalHeadline(p)}</span>
						<span class="proposal-meta">
							{#if p.executor === 'backend_flash'}
								<span class="exec-tag">Flash</span>
							{:else if p.executor === 'mcp'}
								<span class="exec-tag">MCP {p.executor_agent || ''}</span>
							{/if}
							<span class="timestamp">{formatTimestamp(p.created_at)}</span>
						</span>
					</header>

					{#if expandedId === p.id}
						<div class="proposal-body">
							<div class="reason-block">
								<span class="block-label">Reason</span>
								<p>{p.reason}</p>
							</div>

							{#if p.rubric_feedback}
								<div class="reason-block">
									<span class="block-label">Rubric feedback</span>
									<p class="muted">{p.rubric_feedback}</p>
								</div>
							{/if}

							<div class="reason-block">
								<span class="block-label">Payload</span>
								<pre class="payload-dump">{JSON.stringify(p.payload, null, 2)}</pre>
							</div>

							{#if p.affected_node_ids && p.affected_node_ids.length}
								<div class="reason-block">
									<span class="block-label">Affected nodes ({p.affected_node_ids.length})</span>
									<ul class="affected-list">
										{#each p.affected_node_ids as id}<li><code>{id}</code></li>{/each}
									</ul>
								</div>
							{/if}

							{#if p.rubric_version || p.rubric_feedback || p.status}
								<div class="meta-row">
									{#if p.rubric_version}<span>rubric v{p.rubric_version}</span>{/if}
									{#if p.status !== 'pending_review'}<span>status: {p.status}</span>{/if}
									{#if p.reviewed_at}<span>reviewed {formatTimestamp(p.reviewed_at)}</span>{/if}
									{#if p.applied_at}<span>applied {formatTimestamp(p.applied_at)}</span>{/if}
									{#if p.rejection_reason}
										<span>rejected: {p.rejection_reason}</span>
									{/if}
								</div>
							{/if}

							{#if p.status === 'pending_review'}
								{#if rejectingId === p.id}
									<div class="reject-form">
										<textarea
											bind:value={rejectionText}
											placeholder="Why is this being rejected? (5+ chars, becomes audit trail)"
											rows="3"
										></textarea>
										<div class="reject-actions">
											<button class="btn-secondary" on:click={() => { rejectingId = null; rejectionText = ''; errorMessage = ''; }}>Cancel</button>
											<button class="btn-danger" on:click={() => submitReject(p.id)} disabled={actionInFlight.has(p.id)}>
												{actionInFlight.has(p.id) ? 'Rejecting…' : 'Confirm reject'}
											</button>
										</div>
									</div>
								{:else}
									<div class="action-row">
										<button class="btn-danger" on:click={() => { rejectingId = p.id; rejectionText = ''; errorMessage = ''; }} disabled={actionInFlight.has(p.id)}>
											<X size={14} strokeWidth={1.5} />
											<span>Reject</span>
										</button>
										<button class="btn-approve" on:click={() => handleApprove(p.id)} disabled={actionInFlight.has(p.id)}>
											<Check size={14} strokeWidth={1.5} />
											<span>{actionInFlight.has(p.id) ? 'Approving…' : 'Approve & apply'}</span>
										</button>
									</div>
								{/if}
							{/if}
						</div>
					{/if}
				</article>
			{/each}
		</section>
	{/if}
</div>

<style>
	.gardener-page {
		min-height: 100vh;
		background: #FAFAFA;
		padding: 24px 32px 80px;
		font-family: 'Inter', system-ui, -apple-system, sans-serif;
		color: #111827;
	}

	.gardener-header {
		margin-bottom: 24px;
	}

	.header-row {
		display: flex;
		justify-content: space-between;
		align-items: baseline;
		margin-bottom: 8px;
	}

	.gardener-header h1 {
		font-size: 22px;
		font-weight: 700;
		letter-spacing: -0.3px;
		margin: 0;
	}

	.subtitle {
		font-size: 13px;
		color: #6B7280;
		line-height: 1.5;
		margin: 0;
		max-width: 720px;
	}

	.btn-sweep {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		padding: 8px 14px;
		border: 1px solid #D1D5DB;
		border-radius: 6px;
		background: #FFFFFF;
		font-size: 12px;
		font-weight: 500;
		color: #374151;
		cursor: pointer;
		transition: background 150ms ease-out, border-color 150ms ease-out;
	}
	.btn-sweep:hover:not(:disabled) { background: #F9FAFB; border-color: #9CA3AF; }
	.btn-sweep:disabled { opacity: 0.5; cursor: not-allowed; }

	:global(.btn-sweep .spin) { animation: spin 1s linear infinite; }
	@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

	.stats-row {
		display: flex;
		gap: 24px;
		padding: 16px 20px;
		background: #FFFFFF;
		border: 1px solid #E5E7EB;
		border-radius: 8px;
		margin-bottom: 20px;
	}

	.stat { display: flex; flex-direction: column; gap: 4px; min-width: 90px; }
	.stat-label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; color: #9CA3AF; font-weight: 600; }
	.stat-value { font-size: 22px; font-weight: 600; color: #111827; }

	.stat-signal .stat-subvalues {
		display: flex;
		gap: 10px;
		font-size: 14px;
		color: #4B5563;
	}
	.stat-signal .stat-subvalues strong { font-weight: 600; color: #111827; margin-right: 2px; }

	.sweep-result {
		background: #F3F4F6;
		border: 1px solid #E5E7EB;
		border-radius: 6px;
		padding: 10px 14px;
		margin-bottom: 16px;
		display: flex;
		gap: 12px;
		font-size: 12px;
	}
	.sweep-label { color: #6B7280; text-transform: uppercase; letter-spacing: 0.3px; font-weight: 600; }
	.sweep-body { color: #374151; }

	.error-banner {
		background: #FEF2F2;
		border: 1px solid #FECACA;
		color: #991B1B;
		padding: 10px 14px;
		border-radius: 6px;
		margin-bottom: 16px;
		font-size: 12px;
	}

	.tabs {
		display: flex;
		align-items: center;
		gap: 4px;
		border-bottom: 1px solid #E5E7EB;
		margin-bottom: 16px;
		flex-wrap: wrap;
	}

	.tab {
		background: none;
		border: none;
		padding: 10px 14px;
		font-size: 13px;
		color: #6B7280;
		cursor: pointer;
		border-bottom: 2px solid transparent;
		display: inline-flex;
		align-items: center;
		gap: 6px;
		font-weight: 500;
	}
	.tab:hover { color: #111827; }
	.tab.active { color: #111827; border-bottom-color: #111827; }

	.tab-count {
		background: #E5E7EB;
		color: #4B5563;
		font-size: 10px;
		font-weight: 600;
		padding: 2px 6px;
		border-radius: 10px;
	}

	.action-filter {
		margin-left: auto;
		display: flex;
		gap: 4px;
		padding-bottom: 6px;
	}

	.action-pill {
		background: #FFFFFF;
		border: 1px solid #E5E7EB;
		border-radius: 14px;
		padding: 4px 10px;
		font-size: 11px;
		font-weight: 500;
		color: #6B7280;
		cursor: pointer;
	}
	.action-pill:hover { color: #111827; border-color: #9CA3AF; }
	.action-pill.active { background: #111827; color: #FFFFFF; border-color: #111827; }

	.proposal-list {
		display: flex;
		flex-direction: column;
		gap: 8px;
	}

	.state-empty {
		padding: 40px;
		text-align: center;
		color: #9CA3AF;
		font-size: 13px;
	}

	.proposal {
		background: #FFFFFF;
		border: 1px solid #E5E7EB;
		border-radius: 8px;
		transition: border-color 150ms ease-out;
	}
	.proposal:hover { border-color: #D1D5DB; }
	.proposal.expanded { border-color: #9CA3AF; }

	.proposal-head {
		display: flex;
		align-items: center;
		gap: 10px;
		padding: 12px 16px;
		cursor: pointer;
	}

	.chevron { color: #9CA3AF; display: inline-flex; }

	.action-tag {
		font-size: 10px;
		font-weight: 600;
		letter-spacing: 0.5px;
		text-transform: uppercase;
		padding: 2px 6px;
		border-radius: 3px;
		background: #F3F4F6;
		color: #374151;
	}
	.action-tag.action-trunk { background: #F3F4F6; color: #111827; }
	.action-tag.action-reparent { background: #F3F4F6; color: #374151; }
	.action-tag.action-merge { background: #F3F4F6; color: #4B5563; }

	.conf-tag {
		font-size: 10px;
		font-weight: 600;
		text-transform: uppercase;
		padding: 2px 6px;
		border-radius: 3px;
		border: 1px solid #E5E7EB;
		color: #6B7280;
	}
	.conf-tag.conf-high { color: #111827; border-color: #D1D5DB; }
	.conf-tag.conf-medium { color: #6B7280; }

	.proposal-title {
		font-size: 13px;
		font-weight: 500;
		color: #111827;
		flex: 1;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	.proposal-meta {
		display: flex;
		gap: 8px;
		align-items: center;
		font-size: 11px;
		color: #9CA3AF;
		flex-shrink: 0;
	}

	.exec-tag {
		font-size: 10px;
		background: #F3F4F6;
		color: #4B5563;
		padding: 2px 6px;
		border-radius: 3px;
		font-weight: 500;
	}

	.timestamp { font-variant-numeric: tabular-nums; }

	.proposal-body {
		border-top: 1px solid #E5E7EB;
		padding: 16px 20px;
		display: flex;
		flex-direction: column;
		gap: 14px;
	}

	.reason-block { display: flex; flex-direction: column; gap: 4px; }
	.block-label { font-size: 10px; text-transform: uppercase; color: #9CA3AF; font-weight: 600; letter-spacing: 0.5px; }
	.reason-block p { margin: 0; font-size: 13px; color: #111827; line-height: 1.55; }
	.reason-block p.muted { color: #6B7280; font-size: 12px; }

	.payload-dump {
		background: #F9FAFB;
		border: 1px solid #E5E7EB;
		border-radius: 4px;
		padding: 10px 12px;
		font-size: 11px;
		font-family: 'JetBrains Mono', 'SF Mono', Consolas, monospace;
		color: #374151;
		overflow-x: auto;
		max-height: 280px;
		overflow-y: auto;
		margin: 0;
		white-space: pre;
	}

	.affected-list {
		list-style: none;
		padding: 0;
		margin: 0;
		display: flex;
		flex-direction: column;
		gap: 4px;
	}
	.affected-list code {
		font-size: 11px;
		font-family: 'JetBrains Mono', 'SF Mono', Consolas, monospace;
		color: #4B5563;
		background: #F3F4F6;
		padding: 2px 6px;
		border-radius: 3px;
	}

	.meta-row {
		display: flex;
		gap: 16px;
		flex-wrap: wrap;
		font-size: 11px;
		color: #6B7280;
		padding-top: 6px;
		border-top: 1px dashed #E5E7EB;
	}

	.action-row {
		display: flex;
		justify-content: flex-end;
		gap: 8px;
		padding-top: 6px;
	}

	.reject-form {
		display: flex;
		flex-direction: column;
		gap: 8px;
		padding-top: 8px;
	}
	.reject-form textarea {
		width: 100%;
		font-family: inherit;
		font-size: 13px;
		padding: 10px 12px;
		border: 1px solid #D1D5DB;
		border-radius: 4px;
		resize: vertical;
		color: #111827;
		background: #FFFFFF;
	}
	.reject-form textarea:focus { outline: none; border-color: #6B7280; }
	.reject-actions { display: flex; justify-content: flex-end; gap: 8px; }

	.btn-approve, .btn-danger, .btn-secondary {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		padding: 7px 14px;
		border-radius: 5px;
		font-size: 12px;
		font-weight: 500;
		cursor: pointer;
		transition: all 150ms ease-out;
	}
	.btn-approve { background: #111827; color: #FFFFFF; border: 1px solid #111827; }
	.btn-approve:hover:not(:disabled) { background: #000000; }
	.btn-danger { background: #FFFFFF; color: #4B5563; border: 1px solid #D1D5DB; }
	.btn-danger:hover:not(:disabled) { color: #991B1B; border-color: #FCA5A5; }
	.btn-secondary { background: #FFFFFF; color: #6B7280; border: 1px solid #E5E7EB; }
	.btn-secondary:hover:not(:disabled) { color: #111827; border-color: #9CA3AF; }
	.btn-approve:disabled, .btn-danger:disabled, .btn-secondary:disabled { opacity: 0.5; cursor: not-allowed; }

	/* ── Mobile (≤ 768px) ─────────────────────────────────────────────
	   Simple & stark per design direction. Stack everything vertically,
	   let content breathe, give touch targets 40px+ of vertical space.
	*/
	@media (max-width: 768px) {
		.gardener-page { padding: 16px 14px 80px; }

		.header-row {
			flex-direction: column;
			align-items: stretch;
			gap: 12px;
		}
		.gardener-header h1 { font-size: 20px; }
		.subtitle { font-size: 12px; }
		.btn-sweep {
			justify-content: center;
			padding: 11px 14px;
			font-size: 13px;
		}

		/* Stats: 2x2+ grid using flex-wrap so each stat keeps its min-width */
		.stats-row {
			flex-wrap: wrap;
			gap: 14px 18px;
			padding: 14px 16px;
		}
		.stat { min-width: 0; flex: 1 1 calc(50% - 10px); }
		.stat-value { font-size: 18px; }
		.stat-signal .stat-subvalues { font-size: 13px; gap: 8px; }

		/* Tabs row: allow horizontal scroll if pills + filters exceed width */
		.tabs {
			flex-wrap: nowrap;
			overflow-x: auto;
			-webkit-overflow-scrolling: touch;
			scrollbar-width: none;
		}
		.tabs::-webkit-scrollbar { display: none; }
		.tab {
			white-space: nowrap;
			padding: 10px 12px;
			min-height: 40px;
		}

		/* Action filter moves below the tabs on mobile so it gets its own line */
		.action-filter {
			margin-left: 0;
			width: 100%;
			padding: 8px 0;
			overflow-x: auto;
			-webkit-overflow-scrolling: touch;
			scrollbar-width: none;
		}
		.action-filter::-webkit-scrollbar { display: none; }
		.action-pill {
			white-space: nowrap;
			padding: 6px 12px;
			min-height: 32px;
		}

		/* Proposal card: head wraps, meta moves to its own row */
		.proposal-head {
			flex-wrap: wrap;
			padding: 12px 14px;
			gap: 8px;
		}
		.proposal-title {
			flex: 1 1 100%;
			white-space: normal;
			font-size: 13px;
			line-height: 1.4;
			order: 10;
		}
		.proposal-meta {
			width: 100%;
			justify-content: flex-start;
			order: 20;
		}
		.action-tag, .conf-tag { flex-shrink: 0; }

		.proposal-body { padding: 14px 16px; gap: 12px; }
		.payload-dump {
			font-size: 10px;
			max-height: 220px;
			padding: 8px 10px;
		}
		.affected-list code { font-size: 10px; word-break: break-all; }

		/* Action buttons stack full-width on mobile for comfortable tap targets */
		.action-row {
			flex-direction: column-reverse;
			gap: 8px;
		}
		.action-row .btn-approve,
		.action-row .btn-danger,
		.action-row .btn-secondary {
			width: 100%;
			justify-content: center;
			min-height: 42px;
			font-size: 13px;
		}
		.reject-actions {
			flex-direction: column;
		}
		.reject-actions .btn-danger,
		.reject-actions .btn-secondary {
			width: 100%;
			justify-content: center;
			min-height: 42px;
		}

		.sweep-result {
			flex-direction: column;
			gap: 6px;
			align-items: flex-start;
		}
	}

	/* Very small phones — additional squeeze for 320-360px range */
	@media (max-width: 400px) {
		.stat { flex: 1 1 100%; }
		.stats-row { padding: 12px 14px; }
	}
</style>
